import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import { SessionRecord } from "../../../services/master-data.service";
import { studentsApi, Student } from "../students/api";
import reStudyApi, { CreateReStudyPayload } from "./reStudyApi";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddReStudyDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { masters, fetchMasters, fetchSections } = useMasterData();

  // Form State
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [previousSessionId, setPreviousSessionId] = useState("");
  const [previousClassId, setPreviousClassId] = useState("");
  const [newSessionId, setNewSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");

  // Data State
  const [students, setStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [sections, setSections] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchMasters();
      fetchActiveStudents();
    }
  }, [open]);

  const fetchActiveStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await studentsApi.getAll({
        limit: 1000,
        status: "ACTIVE",
      });
      console.log("Re-Study: Fetched students response:", response);
      
      let studentList: Student[] = [];
      if (response && response.data && Array.isArray(response.data)) {
        studentList = response.data;
      } else if (Array.isArray(response)) {
        studentList = response;
      }
      
      console.log("Re-Study: Processed student list:", studentList);
      setStudents(studentList);
    } catch (error) {
      console.error("Failed to fetch students", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    if (classId) {
      (async () => {
        const secs = await fetchSections(classId);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [classId]);

  // When student is selected, pre-fill previous session and class
  useEffect(() => {
    if (selectedStudent) {
      // Find current active session or use the one from student if available
      const activeSession = masters.academicSessions.find((s: SessionRecord) => s.isActive);
      if (activeSession) {
        setPreviousSessionId(activeSession.id);
      }
      setPreviousClassId(selectedStudent.classId);
      
      // Default new session to same as previous for now, or next if available
      // But usually user will select it.
      setClassId(selectedStudent.classId); // Usually re-study means same class
    }
  }, [selectedStudent, masters.academicSessions]);

  const handleSubmit = async () => {
    if (
      !selectedStudent ||
      !previousSessionId ||
      !previousClassId ||
      !newSessionId ||
      !classId ||
      !sectionId
    ) {
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateReStudyPayload = {
        studentId: selectedStudent.id,
        previousSessionId,
        previousClassId,
        newSessionId,
        classId,
        sectionId,
        reason: reason || undefined,
        remarks: remarks || undefined,
      };
      await reStudyApi.create(payload);
      onSuccess();
      onClose();
      // Reset form
      setSelectedStudent(null);
      setPreviousSessionId("");
      setPreviousClassId("");
      setNewSessionId("");
      setClassId("");
      setSectionId("");
      setReason("");
      setRemarks("");
    } catch (error) {
      console.error("Failed to add re-study record", error);
      alert("Failed to add re-study record. Please check if student already has a record for this session.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Re-Study Student</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={students}
              getOptionLabel={(option) =>
                option ? `${option.firstName} ${option.lastName} (${option.admissionNumber})` : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingStudents}
              value={selectedStudent}
              onChange={(_, newValue) => setSelectedStudent(newValue)}
              noOptionsText={loadingStudents ? "Loading..." : "No active students found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Student"
                  required
                  placeholder="Type student name or admission number..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingStudents ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Adm No: {option.admissionNumber} | Scholar No:{" "}
                      {option.scholarNumber || "-"}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Previous Session"
              value={previousSessionId}
              onChange={(e) => setPreviousSessionId(e.target.value)}
              required
            >
              {masters.academicSessions.map((s: SessionRecord) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Previous Class"
              value={previousClassId}
              onChange={(e) => setPreviousClassId(e.target.value)}
              required
            >
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="New Session (Repeat In)"
              value={newSessionId}
              onChange={(e) => setNewSessionId(e.target.value)}
              required
            >
              {masters.academicSessions.map((s: SessionRecord) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="New Class"
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              required
            >
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Section"
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              required
              disabled={!classId}
            >
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Failed in Exams"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting ||
            !selectedStudent ||
            !previousSessionId ||
            !previousClassId ||
            !newSessionId ||
            !classId ||
            !sectionId
          }
        >
          {submitting ? <CircularProgress size={24} /> : "Add Re-Study"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReStudyDialog;
